import { useCallback, useMemo, useState } from "react";
import { showToast } from "@/components/ui/Toast";
import { fmtVal } from "@/lib/orders";
import { useAdminOrders } from "./useAdminOrders";
import type { AdminOrderItem } from "./useAdminOrders";
import {
  PAGE_SIZE,
  statusOptions,
  marketplaceOptions,
  paymentOptions,
  riskOptions,
  slaOptions,
  syncOptions,
  statusColors,
  riskColors,
  slaColors,
  payBadge,
  mktBadge,
  kpiDefs,
  iconBg,
} from "./useOrdersPage.constants";
import type { KpiFilter } from "./useOrdersPage.types";

// Map API DB status → display label used by existing UI constants
const STATUS_DISPLAY: Record<string, string> = {
  UNSHIPPED: "Pending Confirmation",
  CONFIRMED: "Confirmed",
  PACKED: "Packed",
  SHIPPED: "Shipped",
  DELIVERED: "Delivered",
  CANCELLED: "Canceled",
  RETURNED: "Returned",
  PENDING: "Pending Confirmation",
};

// Map display status filter → DB status for server query
const DISPLAY_TO_DB_STATUS: Record<string, string> = {
  New: "UNSHIPPED",
  "Pending Confirmation": "UNSHIPPED",
  Confirmed: "CONFIRMED",
  Packed: "PACKED",
  Shipped: "SHIPPED",
  Delivered: "DELIVERED",
  Canceled: "CANCELLED",
  Returned: "RETURNED",
  "Return Initiated": "RETURNED",
  "Refund Pending": "CANCELLED",
  Refunded: "CANCELLED",
};

// Map KPI filter → server-side params
const KPI_TO_SERVER: Record<
  string,
  Partial<{ status: string; slaStatus: string }>
> = {
  pending: { status: "UNSHIPPED" },
  shipped: { status: "SHIPPED" },
  returns: { status: "RETURNED" },
  refunds: { status: "CANCELLED" },
};

/** Label used only for advanced Payment filter (not shown as a table column). */
function paymentFilterLabel(o: AdminOrderItem): string | null {
  return o.fulfillmentType === "PLATFORM_FULFILLED" ? "Prepaid" : null;
}

function toDisplayOrder(o: AdminOrderItem) {
  return {
    id: o.externalOrderId || o.id,
    _internalId: o.id,
    seller: o.sellerName,
    sellerWsId: o.sellerWorkspaceId.slice(0, 12),
    marketplace: o.marketplace,
    status: STATUS_DISPLAY[o.status] ?? o.status,
    date: o.orderedAt,
    items: o.itemCount ?? 0,
    gmv: o.gmv ?? 0,
    net: o.gmv != null ? Math.round(o.gmv * 0.9 * 100) / 100 : 0,
    risk:
      o.riskLevel === "HIGH"
        ? "High"
        : o.riskLevel === "MEDIUM"
          ? "Medium"
          : "Low",
    issueCount: o.issueCount,
    disputeFlag: o.issueCount > 0,
    syncHealth: "Healthy",
  };
}

export function useOrdersPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [mktFilter, setMktFilter] = useState<string>("All");
  const [sellerFilter, setSellerFilter] = useState<string>("All");
  const [payFilter, setPayFilter] = useState<string>("All");
  const [riskFilter, setRiskFilter] = useState<string>("All");
  const [slaFilter, setSlaFilter] = useState<string>("All");
  const [syncFilter, setSyncFilter] = useState<string>("All");
  const [disputeFilter, setDisputeFilter] = useState<string>("All");
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [kpiFilter, setKpiFilter] = useState<KpiFilter>(null);
  const [page, setPage] = useState(1);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [insightOrderId, setInsightOrderId] = useState<string | null>(null);
  const [bulkModal, setBulkModal] = useState<string | null>(null);

  // Build server-side filter params
  const serverStatus = kpiFilter
    ? KPI_TO_SERVER[kpiFilter]?.status
    : statusFilter !== "All"
      ? DISPLAY_TO_DB_STATUS[statusFilter]
      : undefined;

  const serverSla =
    slaFilter !== "All"
      ? slaFilter === "On track"
        ? "ON_TRACK"
        : slaFilter === "At risk"
          ? "AT_RISK"
          : "BREACHED"
      : undefined;

  const serverRisk =
    riskFilter !== "All" ? riskFilter.toUpperCase() : undefined;

  const sortByMap: Record<string, string> = {
    gmv: "gmv",
    items: "itemCount",
    id: "externalOrderId",
    date: "orderedAt",
  };

  const { data, loading } = useAdminOrders({
    search: search || undefined,
    status: serverStatus,
    provider: mktFilter !== "All" ? mktFilter.toUpperCase() : undefined,
    slaStatus: serverSla,
    riskLevel: serverRisk,
    sortBy: sortCol ? (sortByMap[sortCol] ?? "orderedAt") : "orderedAt",
    sortDir: sortCol ? sortDir : "desc",
    page,
    pageSize: PAGE_SIZE,
  });

  const pageRows = useMemo(
    () =>
      (data?.items ?? []).map((raw) => ({
        raw,
        display: toDisplayOrder(raw),
      })),
    [data],
  );

  // Client-side filters (payment is not on display row; derive from raw API item)
  const visibleItems = useMemo(() => {
    let list = pageRows;
    if (payFilter !== "All") {
      list = list.filter(({ raw }) => paymentFilterLabel(raw) === payFilter);
    }
    if (disputeFilter === "Yes")
      list = list.filter(({ display }) => display.disputeFlag);
    if (disputeFilter === "No")
      list = list.filter(({ display }) => !display.disputeFlag);
    if (syncFilter !== "All")
      list = list.filter(({ display }) => display.syncHealth === syncFilter);
    if (sellerFilter !== "All")
      list = list.filter(({ display }) => display.seller === sellerFilter);
    return list.map(({ display }) => display);
  }, [pageRows, payFilter, disputeFilter, syncFilter, sellerFilter]);

  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;

  // KPI data from API
  const kpi = data?.kpi;
  const ordersKPIs = {
    totalToday: {
      value: kpi?.todayCount ?? 0,
      delta:
        kpi != null
          ? `${kpi.todayCountChangePct >= 0 ? "+" : ""}${kpi.todayCountChangePct.toFixed(1)}%`
          : "—",
    },
    pending: {
      value: kpi?.pendingCount ?? 0,
      delta: "—",
    },
    shippedToday: {
      value: kpi?.shippedTodayCount ?? 0,
      delta:
        kpi != null
          ? `${kpi.shippedTodayChangePct >= 0 ? "+" : ""}${kpi.shippedTodayChangePct.toFixed(1)}%`
          : "—",
    },
    returnsOpen: {
      value: kpi?.returnsOpen ?? 0,
      delta: `+${kpi?.atRiskCount ?? 0}`,
    },
    refunds30d: {
      value: kpi?.cancelled30d ?? 0,
      delta: `${(kpi?.breachedCount ?? 0) > 0 ? "-" : ""}${kpi?.breachedCount ?? 0}`,
    },
    gmv30d: {
      value: kpi?.gmv30d ?? 0,
      delta:
        kpi != null
          ? `${kpi.gmv30dChangePct >= 0 ? "+" : ""}${kpi.gmv30dChangePct.toFixed(1)}%`
          : "—",
    },
  };

  const sellerList = [
    ...new Set(pageRows.map(({ display }) => display.seller)),
  ].sort();

  const fmtDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const toggleSort = (col: string) => {
    if (sortCol === col) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortCol(col);
      setSortDir("asc");
    }
    setPage(1);
  };

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === visibleItems.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(visibleItems.map((o) => o.id)));
    }
  };

  const resetFilters = useCallback(() => {
    setSearch("");
    setStatusFilter("All");
    setMktFilter("All");
    setSellerFilter("All");
    setPayFilter("All");
    setRiskFilter("All");
    setSlaFilter("All");
    setSyncFilter("All");
    setDisputeFilter("All");
    setKpiFilter(null);
    setPage(1);
  }, []);

  const handleKPIClick = (filter: KpiFilter) => {
    if (filter) {
      setKpiFilter((prev) => (prev === filter ? null : filter));
      setPage(1);
    }
  };

  const handleBulkAction = (action: string) => setBulkModal(action);

  const confirmBulk = () => {
    if (!bulkModal) return;
    showToast.success(`${bulkModal}`, `Applied to ${selected.size} orders.`);
    setBulkModal(null);
    setSelected(new Set());
  };

  const isUp = (d: string) => !d.startsWith("-");

  return {
    // state
    search,
    statusFilter,
    mktFilter,
    sellerFilter,
    payFilter,
    riskFilter,
    slaFilter,
    syncFilter,
    disputeFilter,
    showAdvanced,
    setShowAdvanced,
    kpiFilter,
    page,
    sortCol,
    sortDir,
    selected,
    insightOrderId,
    bulkModal,
    loading,

    // derived
    filtered: visibleItems,
    paginated: visibleItems,
    totalPages,
    totalCount,
    sellerList,

    // constants / config
    PAGE_SIZE,
    statusOptions,
    marketplaceOptions,
    paymentOptions,
    riskOptions,
    slaOptions,
    syncOptions,
    statusColors,
    riskColors,
    slaColors,
    payBadge,
    mktBadge,
    kpiDefs,
    iconBg,
    ordersKPIs,

    // helpers
    fmtVal,
    fmtDate,
    isUp,

    // actions
    setSearch,
    setStatusFilter,
    setMktFilter,
    setSellerFilter,
    setPayFilter,
    setRiskFilter,
    setSlaFilter,
    setSyncFilter,
    setDisputeFilter,
    setKpiFilter,
    setPage,
    setSelected,
    setInsightOrderId,
    setBulkModal,
    toggleSort,
    toggleSelect,
    toggleAll,
    resetFilters,
    handleKPIClick,
    handleBulkAction,
    confirmBulk,
  };
}
